<script>
  // The Busy / Queue page. Deliberately NOT built on JobList: it is
  // socket-driven, has no fetch, and shows two lists rather than one.
  import { onDestroy, onMount } from "svelte";
  import TabBar from "../TabBar.svelte";
  import QueueCard from "../FileCard/QueueCard.svelte";
  import BusyCard from "../FileCard/BusyCard.svelte";
  import PageSection from "../UI/PageSection.svelte";
  import EmptyState from "../UI/EmptyState.svelte";
  import LoadingState from "../UI/LoadingState.svelte";
  import { getSocket, closeSocket } from "../../lib/socket.js";
  import { refreshCounts } from "../../lib/counts.js";

  let queue_files = [];
  let busy_file = [];
  let loading = true;

  const socket = getSocket();
  let interval;

  // Polling, over a websocket, which looks wrong until you know why.
  //
  // Solver stdout genuinely is pushed (solverFeedback). But a job MOVING
  // between queued, busy and finished happens in HTTP route handlers and in
  // the queue poller, neither of which notifies the socket layer -- so there is
  // no event to push. Polling is the honest way to reflect that. Making it
  // push means giving those writers a way to signal, which is a larger change
  // than it looks.
  //
  // getBusy is also what joins this client to the running job's room, so
  // BusyCard's stream depends on it having been called at least once.
  const POLL_MS = 3000;

  socket.on("receiveBusy", (response) => {
    // Only re-ask for the tab counts when the page's contents actually
    // changed. The poll runs every three seconds and the counts are a database
    // query -- asking on every tick would be twenty requests a minute to
    // redraw numbers that mostly have not moved.
    const changed =
      response.queue.length !== queue_files.length ||
      response.busy.length !== busy_file.length;

    queue_files = response.queue;
    busy_file = response.busy;

    if (changed) refreshCounts();
    loading = false;
  });

  onMount(() => {
    socket.emit("getBusy");
    interval = setInterval(() => socket.emit("getBusy"), POLL_MS);
  });

  onDestroy(() => {
    clearInterval(interval);
    closeSocket();
  });

  // Dequeuing removes the card on the next poll anyway; this just makes it
  // immediate rather than up to three seconds later.
  function refresh() {
    socket.emit("getBusy");
  }
</script>

<div class="flex flex-col gap-4">
  <TabBar />

  {#if loading}
    <LoadingState rows={2} />
  {:else}
    {#if busy_file.length !== 0}
      <PageSection title="Running">
        {#each busy_file as file (file.id)}
          <BusyCard fileId={file.id} filename={file.file_name} />
        {/each}
      </PageSection>
    {/if}

    {#if queue_files.length !== 0}
      <PageSection title="Queued" count={queue_files.length}>
        {#each queue_files as file (file.id)}
          <QueueCard
            fileId={file.id}
            filename={file.file_name}
            uploadedDateTime={file.upload_date}
            on:changed={refresh}
          />
        {/each}
      </PageSection>
    {/if}

    {#if queue_files.length === 0 && busy_file.length === 0}
      <EmptyState
        icon="fa-hourglass-half"
        title="Nothing is queued"
        hint="Send a ready job to the solver and it will appear here."
      />
    {/if}
  {/if}
</div>
